/**
 * cart-drawer-footer.js
 * =====================
 * GC-223. The Route app (shopify-widget.route.com, on the real store) hides the drawer's Checkout,
 * shows its own copy, and adds a "Checkout without Coverage" line under it. Around every cart change
 * it takes the line away and puts it back up to 0.5 s later: when GC-208 marks Checkout busy, after
 * its own cart refresh, and after each redraw, which brings a footer without the line. The footer is
 * pinned to the bottom of the screen, so each time its top edge, the subtotal and Checkout jumped by
 * the line's height.
 *
 * Checkout and Route's line share one block, .cart-drawer__checkout (snippets/cart.liquid). This keeps
 * the block at the tallest height it has had while the drawer is open, as --cart-drawer-checkout-h on
 * the drawer's .cart--root, which a redraw keeps (it replaces only .cart--form inside it).
 * assets/cart-drawer.css makes that the block's min-height.
 *
 * Feature 047: the /cart page's summary card holds Checkout in the same block, and everything under
 * it jumped the same way, so the page's .cart--root gets the same hold (it never closes, so only a
 * new width starts it again).
 *
 * GC-236: a theme editor section reload replaces the root, so each new root is held again.
 */
(function () {
  "use strict";

  // Bind once, even if this file runs again; the shopify:section:load listener below holds new roots.
  if (window.GCCartDrawerFooter) {
    return;
  }
  window.GCCartDrawerFooter = true;

  if (!window.ResizeObserver) {
    return;
  }

  // Roots already held, so a reload of another section never adds a second hold.
  var held = new WeakSet();

  /**
   * Keeps one cart's Checkout block at the tallest height it has had.
   *
   * @param {Element} root - The cart's .cart--root, which a redraw keeps.
   * @param {?Element} viewport - The OffCanvas viewport, for the drawer: once it closes, the drawer
   *   starts again from what it shows on the next open. Null for the page.
   * @returns {void}
   */
  function hold(root, viewport) {
    if (held.has(root)) {
      return;
    }
    held.add(root);

    var tallest = 0;
    var width = window.innerWidth;

    // Only ever raises the height: lowering it here would resize the block inside its own callback.
    // shortcut: Route hides its line while the shopper has protection turned off; its space then stays
    // empty until the drawer closes or the width changes. Watch Route's protection state if that shows.
    var sizes = new ResizeObserver(function (entries) {
      entries.forEach(function (entry) {
        var block = entry.target;
        if (!block.isConnected) {
          sizes.unobserve(block);
          return;
        }
        // The block has no padding or border, so this is the box its min-height sets. Unrounded,
        // so the held height is never half a pixel short.
        var height = block.getBoundingClientRect().height;
        if (height > tallest) {
          tallest = height;
          root.style.setProperty("--cart-drawer-checkout-h", tallest + "px");
        }
      });
    });

    /**
     * Watches the block the cart shows now.
     *
     * @returns {void}
     */
    function watch() {
      var block = root.querySelector(".cart-drawer__checkout");
      if (block) {
        sizes.observe(block);
      }
    }

    /**
     * Lets the block take its own height again; the observer then records it as the tallest.
     *
     * @returns {void}
     */
    function reset() {
      tallest = 0;
      root.style.removeProperty("--cart-drawer-checkout-h");
    }

    watch();
    // Every redraw replaces .cart--form, a direct child of the root, and the block with it.
    new MutationObserver(watch).observe(root, { childList: true });
    if (viewport) {
      new MutationObserver(function () {
        if (viewport.getAttribute("data-off-canvas--state") === "closed") {
          reset();
        }
      }).observe(viewport, { attributes: true, attributeFilter: ["data-off-canvas--state"] });
    }
    // A new width can wrap Route's line onto more or fewer rows.
    window.addEventListener("resize", function onResize() {
      // A section reload took this root away: the new one has its own hold.
      if (!root.isConnected) {
        window.removeEventListener("resize", onResize);
        return;
      }
      if (window.innerWidth !== width) {
        width = window.innerWidth;
        reset();
      }
    });
  }

  /**
   * Holds the drawer's and the page's root, whichever the page shows and are not held yet.
   *
   * @returns {void}
   */
  function holdAll() {
    var drawer = document.querySelector('.off-canvas--right-sidebar .cart--root[data-is-drawer="true"]');
    var page = document.querySelector('.cart--root[data-view="page"]');
    if (drawer) {
      hold(drawer, document.querySelector(".off-canvas--viewport"));
    }
    if (page) {
      hold(page, null);
    }
  }

  holdAll();
  document.addEventListener("shopify:section:load", holdAll);
})();
